import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckSquare, ChevronLeft, ChevronRight, SlidersHorizontal, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../hooks/useAuth';
import { useTaskList, useBulkReassign } from '../../api/tasks';
import { useProjectList } from '../../api/projects';
import { useEngineerList } from '../../api/engineers';
import { useEscalations } from '../../api/escalations';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { TablePageSkeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import ErrorState from '../../components/ui/ErrorState';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatDate, daysUntil } from '../../lib/dates';
import type { EscalationLevel, TaskStatus, TaskType } from '../../types/api';

const PAGE_SIZE = 20;

const STATUS_VARIANT: Record<TaskStatus, 'green' | 'red' | 'gray'> = {
  active: 'green', blocked: 'red', done: 'gray',
};
const ESC_VARIANT: Record<EscalationLevel, 'yellow' | 'red'> = {
  TMinus3: 'yellow', TMinus1: 'red', Overdue: 'red',
};
const ESC_LABEL: Record<EscalationLevel, string> = {
  TMinus3: 'T-3', TMinus1: 'T-1', Overdue: 'Overdue',
};

const SELECT = 'h-8 rounded-md border-0 bg-transparent pl-2 pr-7 text-sm focus:outline-none focus:ring-0 cursor-pointer text-foreground';

export default function TaskListPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const isPm = can('project_manager');

  const [projectId,   setProjectId]   = useState('');
  const [assigneeId,  setAssigneeId]  = useState('');
  const [status,      setStatus]      = useState('');
  const [taskType,    setTaskType]    = useState<TaskType | ''>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkTarget,  setBulkTarget]  = useState('');
  // Cursor history: index 0 = first page (undefined cursor), each push moves forward
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([undefined]);
  const currentCursor = cursorHistory[cursorHistory.length - 1];
  const page = cursorHistory.length; // 1-based display

  const resetPagination = () => setCursorHistory([undefined]);

  const { data: tasks, isLoading, error, refetch } = useTaskList({
    projectId:  projectId  || undefined,
    assigneeId: assigneeId || undefined,
    status:     status     || undefined,
    taskType:   taskType   || undefined,
    cursor:     currentCursor,
    limit:      PAGE_SIZE,
  });

  const { data: projects }    = useProjectList();
  const { data: engineers }   = useEngineerList();
  const { data: escalations } = useEscalations();
  const bulkReassign          = useBulkReassign();

  const escalationMap = useMemo(() => {
    const m = new Map<string, EscalationLevel>();
    escalations?.forEach((e) => m.set(e.taskId, e.level));
    return m;
  }, [escalations]);

  const items         = tasks?.items ?? [];
  const allSelectable = items.filter((t) => t.status !== 'done');
  const allSelected   = allSelectable.length > 0 && allSelectable.every((t) => selectedIds.has(t.id));

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(allSelectable.map((t) => t.id)));
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkReassign = () => {
    if (!bulkTarget) { toast.error('Select a target engineer first.'); return; }
    bulkReassign.mutate(
      { taskIds: Array.from(selectedIds), targetEngineerId: bulkTarget },
      {
        onSuccess: (result) => {
          const eng = engineers?.find((e) => e.id === bulkTarget)?.name ?? 'engineer';
          if (result.triggeredOverworkWarning) {
            toast.warning(`${result.tasksReassigned} tasks moved to ${eng}. ⚠ Overwork signals tripped.`);
          } else {
            toast.success(`${result.tasksReassigned} tasks reassigned to ${eng}.`);
          }
          setSelectedIds(new Set()); setBulkTarget('');
        },
      },
    );
  };

  const hasFilters = !!(projectId || assigneeId || status || taskType);
  const clearFilters = () => {
    setProjectId(''); setAssigneeId(''); setStatus(''); setTaskType('');
    resetPagination();
  };

  return (
    <div>
      <PageHeader
        title="Tasks"
        actions={isPm ? <Button size="sm" onClick={() => navigate('/tasks/new')}>New task</Button> : undefined}
      />

      {/* Filters */}
      <div className="mb-4 flex items-center gap-2 rounded-xl border bg-card px-3 py-2 shadow-sm">
        <SlidersHorizontal className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <div className="flex flex-1 flex-wrap items-center gap-0.5">
          <div className="flex items-center">
            <span className="px-2 text-xs font-medium text-muted-foreground">Project</span>
            <select className={SELECT} value={projectId} onChange={(e) => { setProjectId(e.target.value); resetPagination(); }}>
              <option value="">All</option>
              {projects?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="mx-1 h-4 w-px bg-border" />
          {isPm && (
            <>
              <div className="flex items-center">
                <span className="px-2 text-xs font-medium text-muted-foreground">Assignee</span>
                <select className={SELECT} value={assigneeId} onChange={(e) => { setAssigneeId(e.target.value); resetPagination(); }}>
                  <option value="">All</option>
                  {engineers?.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div className="mx-1 h-4 w-px bg-border" />
            </>
          )}
          <div className="flex items-center">
            <span className="px-2 text-xs font-medium text-muted-foreground">Status</span>
            <select className={SELECT} value={status} onChange={(e) => { setStatus(e.target.value); resetPagination(); }}>
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="blocked">Blocked</option>
              <option value="done">Done</option>
            </select>
          </div>
          <div className="mx-1 h-4 w-px bg-border" />
          <div className="flex items-center">
            <span className="px-2 text-xs font-medium text-muted-foreground">Type</span>
            <select className={SELECT} value={taskType} onChange={(e) => { setTaskType(e.target.value as TaskType | ''); resetPagination(); }}>
              <option value="">All</option>
              <option value="Feature">Feature</option>
              <option value="Bug">Bug</option>
              <option value="Test">Test</option>
              <option value="Review">Review</option>
              <option value="Chore">Chore</option>
            </select>
          </div>
        </div>
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="ml-auto flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
      </div>

      {isLoading && <TablePageSkeleton cols={6} hasAction={false} />}
      {error     && <ErrorState onRetry={refetch} />}

      {!isLoading && !error && (
        <Card>
          {items.length === 0 ? (
            <EmptyState
              icon={CheckSquare}
              title={hasFilters ? 'No tasks match your filters' : 'No tasks yet'}
              description={hasFilters ? 'Try adjusting or clearing your filters.' : 'Create a task to get started.'}
              action={
                hasFilters
                  ? <button onClick={clearFilters} className="text-xs text-primary hover:underline">Clear filters</button>
                  : isPm
                    ? <Button size="sm" onClick={() => navigate('/tasks/new')}>New task</Button>
                    : undefined
              }
            />
          ) : (
            <>
              <div className="flex items-center justify-between border-b px-4 py-2.5">
                <span className="text-xs text-muted-foreground">
                  {items.length === PAGE_SIZE ? `${items.length}+ tasks` : `${items.length} task${items.length !== 1 ? 's' : ''}`}
                  {hasFilters && <span className="ml-1 text-primary">· filtered</span>}
                </span>
                {page > 1 && <span className="text-xs text-muted-foreground">Page {page}</span>}
              </div>
            <Table>
              <TableHeader>
                <TableRow>
                  {isPm && (
                    <TableHead className="w-10">
                      <input
                        type="checkbox" checked={allSelected} onChange={toggleAll}
                        className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                        aria-label="Select all tasks"
                      />
                    </TableHead>
                  )}
                  <TableHead>Title</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead className="text-right">Pts</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((task) => {
                  const days = daysUntil(task.dueDate);
                  const proj = projects?.find((p) => p.id === task.projectId);
                  const esc  = escalationMap.get(task.id);
                  return (
                    <TableRow
                      key={task.id}
                      className={cn('cursor-pointer', selectedIds.has(task.id) && 'bg-primary/5')}
                      onClick={() => navigate(`/tasks/${task.id}`)}
                    >
                      {isPm && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {task.status !== 'done' && (
                            <input
                              type="checkbox" checked={selectedIds.has(task.id)} onChange={() => toggleOne(task.id)}
                              className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                              aria-label={`Select ${task.title}`}
                            />
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Link to={`/tasks/${task.id}`} className="font-medium text-foreground hover:text-primary" onClick={(e) => e.stopPropagation()}>
                            {task.title}
                          </Link>
                          {esc && <Badge label={ESC_LABEL[esc]} variant={ESC_VARIANT[esc]} />}
                        </div>
                        {task.blockerReason && (
                          <p className="mt-0.5 truncate text-xs text-destructive">{task.blockerReason}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{proj?.name ?? '—'}</TableCell>
                      <TableCell><Badge label={task.status} variant={STATUS_VARIANT[task.status]} /></TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(task.dueDate)}
                        {days < 0 && <span className="ml-1 text-xs text-destructive">({Math.abs(days)}d late)</span>}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">{task.points}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </>
          )}
        </Card>
      )}

      {/* Pagination */}
      {!isLoading && !error && (tasks?.hasMore || page > 1) && (
        <div className="mt-3 flex items-center justify-end gap-1">
          <Button
            size="sm" variant="ghost"
            disabled={page === 1}
            onClick={() => { setSelectedIds(new Set()); setCursorHistory((h) => h.slice(0, -1)); }}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <Button
            size="sm" variant="ghost"
            disabled={!tasks?.hasMore}
            onClick={() => {
              if (tasks?.nextCursor) {
                setSelectedIds(new Set());
                setCursorHistory((h) => [...h, tasks.nextCursor!]);
              }
            }}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Bulk reassign bar */}
      {isPm && selectedIds.size > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-10 border-t bg-card px-6 py-3 shadow-lg">
          <div className="mx-auto flex max-w-4xl items-center gap-3">
            <span className="text-sm font-medium">
              {selectedIds.size} task{selectedIds.size !== 1 ? 's' : ''} selected
            </span>
            <select className={`${SELECT} ml-auto`} value={bulkTarget} onChange={(e) => setBulkTarget(e.target.value)}>
              <option value="">Reassign to…</option>
              {engineers?.filter((e) => e.isActive).map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
            <Button size="sm" onClick={handleBulkReassign} loading={bulkReassign.isPending} disabled={!bulkTarget}>
              Reassign
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
